import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { MASTER_TEMPLATE_FILE_PATH, downloadFile } from '@nxsys/constants/templateFiles';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import ImportEmployeeTab from './ImportEmployeeTab';
import AgencyUploadTab from './AgencyUploadTab';
import { AGENCY_UPLOAD_FILE_TYPE } from '../constants';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';

enum IMPORT_EMPLOYEE_TAB {
  IMPORTED = 'IMPORTED',
  AGENCY_UPLOAD = 'AGENCY_UPLOAD',
}

const ImportEmployeeListing = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const importFileRef = useRef(null);
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const tabs = useMemo(
    () => [
      {
        tab: messages['common.imported'],
        key: IMPORT_EMPLOYEE_TAB.IMPORTED,
      },
      {
        tab: messages['common.agencyUpload'],
        key: IMPORT_EMPLOYEE_TAB.AGENCY_UPLOAD,
      },
    ],
    [],
  );

  const [activeTab, setActiveTab] = useState<IMPORT_EMPLOYEE_TAB>(
    IMPORT_EMPLOYEE_TAB.IMPORTED,
  );

  const handleTabChange = useCallback((key: IMPORT_EMPLOYEE_TAB) => {
    setActiveTab(key);
    navigate('/import/employees-subcontractors', {
      query: {
        tab: key,
      },
    });
  }, []);

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['common.import']}
        separator="-"
        title={messages['common.employeeAndSubcontractor']}
      />
    );
  }, [messages]);

  const renderRightContent = useCallback(() => {
    return (
      <div className="flex items-center gap-2">
        <AppButton
          variant="outlined"
          name={messages['common.downloadMasterTemplate']}
          onClick={() => {
            let link = '';
            if (!isBureau) {
              link = (isIreland
          ? MASTER_TEMPLATE_FILE_PATH.umbrellaIreland.importEmployee
          : MASTER_TEMPLATE_FILE_PATH.umbrella.importEmployee);
            } else if (isIreland) {
              link = MASTER_TEMPLATE_FILE_PATH.ireland.importEmployee;
            } else {
              link = MASTER_TEMPLATE_FILE_PATH.uk.importEmployee;
            }
            downloadFile(link);
          }}
          leftIcon={
            <Icons.IconDownload
              color={defaultTheme.theme.palette.primary.main}
            />
          }
        />
        {activeTab === IMPORT_EMPLOYEE_TAB.IMPORTED && (
          <AppButton
            name={messages['common.importFile']}
            onClick={() => (importFileRef?.current as any)?.open()}
            leftIcon={
              <Icons.IconFileImport
                color={defaultTheme.theme.palette.white}
              />
            }
          />
        )}
      </div>
    );
  }, [messages, activeTab]);

  useEffect(() => {
    if (tab) {
      setActiveTab(tab as IMPORT_EMPLOYEE_TAB);
    }
  }, [tab]);

  const tabContent = useMemo(() => {
    switch (activeTab) {
      case IMPORT_EMPLOYEE_TAB.IMPORTED:
        return <ImportEmployeeTab importFileRef={importFileRef} />;
      case IMPORT_EMPLOYEE_TAB.AGENCY_UPLOAD:
        return (
          <AgencyUploadTab fileType={AGENCY_UPLOAD_FILE_TYPE.SUBCONTRACTORS} />
        );
      default:
        return <></>;
    }
  }, [activeTab]);

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        {!isBureau ? (
          <AppCardTabListContainer
            tabList={tabs}
            activeTab={activeTab}
            handleTabChange={handleTabChange}
          >
            {tabContent}
          </AppCardTabListContainer>
        ) : (
          <ImportEmployeeTab importFileRef={importFileRef} />
        )}
      </AppCard>
    </AppsContainer>
  );
};

export default React.memo(ImportEmployeeListing);
